/**
 * VEDANGJYOTISHAM V6.0 — RELOCATION & GEOGRAPHY INTELLIGENCE ENGINE
 * RG-HF-V6.0, RG-LEVEL2-CLOSURE-V6.0 & RG-HORIZON-EXT-V6.0
 * 
 * Strict architectural separation of:
 * - Birth Place (Documented place of birth ONLY)
 * - Current Residence (User-provided location attribute; NEVER inferred from Birth Place)
 * - Previous Residence(s) & Historical Residence Chain
 * - Direction Reference Origin (Explicitly selected: Current Residence | Birthplace | Origin)
 * 
 * Extended Horizon Discovery (RG-HORIZON-EXT-V6.0):
 * - Selectable analysis horizons: 7D, 30D, 90D, 6M, 1Y, 3Y, 5Y, 10Y
 * - Strict separation: Focus Date = Current State reference, Horizon = Future Discovery Interval
 * - Multi-event discovery windows for horizons > 90D
 * - Adaptive temporal resolution: Prāṇa/Sūkṣma at 7D/30D to MD/major AD at 10Y
 * - Decreasing precision appropriate to temporal distance
 * - Full BTR sensitivity per event
 */

import { CompleteKundali, Language, PlanetName } from '../../types/kundali';
import { getActiveDasha, getHouseBindus } from './questions/questionEvaluationHelpers';
import {
  LocationStatus,
  LocationRecord,
  RelocationLocationProfile,
  MovementType,
  ResidenceChangeLikelihood,
  GeographicScale,
  CardinalDirection,
  DirectionReferencePoint,
  BirthplaceRelationship,
  RelocationDriver,
  RelocationSequence,
  DurationClass,
  RelocationCurrentStatus,
  RelocationTimeHorizon,
  ForeignQuadAssessment,
  RelocationAIGatewayPayload,
  RelocationEventSignature,
  RelocationCalculationOptions,
  RelocationDurationDetails,
  PeakDateDetails,
  CareerResidenceOverlap,
  RelocationEvidenceItem,
  D4ChaturthamshaAnalysis,
  GeographicContextClassification,
  DirectionProvenanceDetails,
  RelocationEventWindow,
  RelocationHorizonDiscovery
} from '../../types/relocationGeography';

// Re-export types for consumers
export type {
  LocationStatus,
  LocationRecord,
  RelocationLocationProfile,
  MovementType,
  ResidenceChangeLikelihood,
  GeographicScale,
  CardinalDirection,
  DirectionReferencePoint,
  DirectionReferencePoint as DirectionReference,
  BirthplaceRelationship,
  RelocationDriver,
  RelocationSequence,
  DurationClass,
  RelocationCurrentStatus,
  RelocationTimeHorizon,
  ForeignQuadAssessment,
  RelocationAIGatewayPayload,
  RelocationEventSignature,
  RelocationCalculationOptions,
  RelocationDurationDetails,
  PeakDateDetails,
  CareerResidenceOverlap,
  RelocationEvidenceItem,
  D4ChaturthamshaAnalysis,
  GeographicContextClassification,
  DirectionProvenanceDetails,
  RelocationEventWindow,
  RelocationHorizonDiscovery
};

// Classical Direction Mapping for Signs (Brihat Parashara Hora Shastra Ch. 4)
const SIGN_DIRECTIONS: Record<string, CardinalDirection> = {
  Aries: 'EAST',
  Leo: 'EAST',
  Sagittarius: 'EAST',
  Taurus: 'SOUTH',
  Virgo: 'SOUTH',
  Capricorn: 'SOUTH',
  Gemini: 'WEST',
  Libra: 'WEST',
  Aquarius: 'WEST',
  Cancer: 'NORTH',
  Scorpio: 'NORTH',
  Pisces: 'NORTH'
};

const GRAHA_DIRECTIONS: Record<PlanetName, CardinalDirection> = {
  Sun: 'EAST',
  Moon: 'NORTH_WEST',
  Mars: 'SOUTH',
  Mercury: 'NORTH',
  Jupiter: 'NORTH_EAST',
  Venus: 'SOUTH_EAST',
  Saturn: 'WEST',
  Rahu: 'SOUTH_WEST',
  Ketu: 'NORTH_WEST',
  Ascendant: 'EAST'
};

const ZODIAC_SIGNS = [
  'Aries', 'Taurus', 'Gemini', 'Cancer',
  'Leo', 'Virgo', 'Libra', 'Scorpio',
  'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'
];

const SIGN_LORDS: Record<string, PlanetName> = {
  Aries: 'Mars',
  Taurus: 'Venus',
  Gemini: 'Mercury',
  Cancer: 'Moon',
  Leo: 'Sun',
  Virgo: 'Mercury',
  Libra: 'Venus',
  Scorpio: 'Mars',
  Sagittarius: 'Jupiter',
  Capricorn: 'Saturn',
  Aquarius: 'Saturn',
  Pisces: 'Jupiter'
};

const DASHA_CYCLE_ORDER: { planet: PlanetName; years: number }[] = [
  { planet: 'Ketu', years: 7 },
  { planet: 'Venus', years: 20 },
  { planet: 'Sun', years: 6 },
  { planet: 'Moon', years: 10 },
  { planet: 'Mars', years: 7 },
  { planet: 'Rahu', years: 18 },
  { planet: 'Jupiter', years: 16 },
  { planet: 'Saturn', years: 19 },
  { planet: 'Mercury', years: 17 }
];

const MONTH_NAMES_EN = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTH_NAMES_HI = ['जनवरी', 'फ़रवरी', 'मार्च', 'अप्रैल', 'मई', 'जून', 'जुलाई', 'अगस्त', 'सितंबर', 'अक्टूबर', 'नवंबर', 'दिसंबर'];

function formatIsoDate(d: Date, lang: Language): string {
  const day = d.getDate().toString().padStart(2, '0');
  const month = lang === 'hi' ? MONTH_NAMES_HI[d.getMonth()] : MONTH_NAMES_EN[d.getMonth()];
  const year = d.getFullYear();
  return `${day} ${month} ${year}`;
}

/**
 * Calculates calendar-safe horizon end date from a focus date
 */
export function calculateHorizonEndDate(focusDate: Date, horizon: RelocationTimeHorizon): Date {
  const d = new Date(focusDate.getTime());
  switch (horizon) {
    case '7D':
      return new Date(focusDate.getTime() + 7 * 86400000);
    case '30D':
      d.setMonth(d.getMonth() + 1);
      return d;
    case '90D':
      d.setMonth(d.getMonth() + 3);
      return d;
    case '6M':
      d.setMonth(d.getMonth() + 6);
      return d;
    case '1Y':
      d.setFullYear(d.getFullYear() + 1);
      return d;
    case '3Y':
      d.setFullYear(d.getFullYear() + 3);
      return d;
    case '5Y':
      d.setFullYear(d.getFullYear() + 5);
      return d;
    case '10Y':
      d.setFullYear(d.getFullYear() + 10);
      return d;
    default:
      d.setFullYear(d.getFullYear() + 1);
      return d;
  }
}

/**
 * Builds deterministic cache key isolated by chart, focus date, horizon, and location identity
 */
export function generateRelocationCacheKey(
  chartId: string,
  focusDate: string,
  horizon: RelocationTimeHorizon,
  referenceLocationValue: string | null,
  originValue: string | null,
  destinationValue: string | null,
  version = 'V6.0-RG-HORIZON'
): string {
  const ref = referenceLocationValue ? referenceLocationValue.toLowerCase().trim() : 'no_ref';
  const orig = originValue ? originValue.toLowerCase().trim() : 'no_orig';
  const dest = destinationValue ? destinationValue.toLowerCase().trim() : 'no_dest';
  return `reloc_${version}_${chartId}_${focusDate}_h:${horizon}_ref:${ref}_orig:${orig}_dest:${dest}`;
}

/**
 * Resolves or builds a validated RelocationLocationProfile following Section 1-3 rules
 */
export function buildRelocationLocationProfile(
  kundali: CompleteKundali,
  options?: RelocationCalculationOptions
): RelocationLocationProfile {
  const birthPlaceName = kundali.birthDetails?.placeName || 'Unknown Birthplace';
  const birthRecord: LocationRecord = {
    locationType: 'BIRTH_PLACE',
    locationValue: birthPlaceName,
    latitude: kundali.birthDetails?.latitude,
    longitude: kundali.birthDetails?.longitude,
    source: 'BIRTH_RECORD',
    sourceTimestamp: kundali.birthDetails?.date ? `${kundali.birthDetails.date}T${kundali.birthDetails.time || '00:00:00'}` : new Date().toISOString(),
    confirmedByUser: true,
    effectiveFrom: kundali.birthDetails?.date
  };

  // Determine Current Residence Status
  let currentResidenceStatus: LocationStatus = 'NOT_PROVIDED';
  let currentResidence: LocationRecord | null = null;

  if (options?.userDeclinedLocation) {
    currentResidenceStatus = 'USER_DECLINED';
  } else if (options?.currentResidenceStatus) {
    currentResidenceStatus = options.currentResidenceStatus;
    if (currentResidenceStatus === 'KNOWN' && options.currentResidence) {
      currentResidence = {
        locationType: 'CURRENT_RESIDENCE',
        locationValue: options.currentResidence.trim(),
        source: 'USER_PROVIDED',
        sourceTimestamp: new Date().toISOString(),
        confirmedByUser: true,
        effectiveTo: 'CURRENT'
      };
    }
  } else if (options?.currentResidence && options.currentResidence.trim().length > 0) {
    currentResidenceStatus = 'KNOWN';
    currentResidence = {
      locationType: 'CURRENT_RESIDENCE',
      locationValue: options.currentResidence.trim(),
      source: 'USER_PROVIDED',
      sourceTimestamp: new Date().toISOString(),
      confirmedByUser: true,
      effectiveTo: 'CURRENT'
    };
  } else if (options?.locationProfile?.currentResidence) {
    currentResidence = options.locationProfile.currentResidence;
    currentResidenceStatus = options.locationProfile.currentResidenceStatus || 'KNOWN';
  }

  // Location history & previous residences
  const locationHistory: LocationRecord[] = options?.locationProfile?.locationHistory || [];
  const previousResidence: LocationRecord[] = options?.locationProfile?.previousResidence || [];

  // Selected Origin & Destination
  let selectedOrigin: LocationRecord | null = null;
  if (options?.customOrigin && options.customOrigin.trim().length > 0) {
    selectedOrigin = {
      locationType: 'SELECTED_ORIGIN',
      locationValue: options.customOrigin.trim(),
      source: 'EXPLICIT_SELECTION',
      sourceTimestamp: new Date().toISOString(),
      confirmedByUser: true
    };
  } else if (options?.locationProfile?.selectedOrigin) {
    selectedOrigin = options.locationProfile.selectedOrigin;
  }

  const selectedDestination: LocationRecord | null = options?.locationProfile?.selectedDestination || null;

  // Resolve reference location based on requested originReference
  const requestedRef = options?.originReference || 'CURRENT_RESIDENCE';
  let referenceLocation: LocationRecord | null = null;

  if (requestedRef === 'CURRENT_RESIDENCE') {
    if (currentResidenceStatus === 'KNOWN' && currentResidence) {
      referenceLocation = currentResidence;
    }
  } else if (requestedRef === 'BIRTHPLACE') {
    referenceLocation = birthRecord;
  } else if (requestedRef === 'USER_SELECTED' && selectedOrigin) {
    referenceLocation = selectedOrigin;
  } else if (requestedRef === 'PREVIOUS_RESIDENCE') {
    if (options?.historicalDate && locationHistory.length > 0) {
      const match = locationHistory.find(h => {
        const from = h.effectiveFrom || '0000-00-00';
        const to = h.effectiveTo === 'CURRENT' ? '9999-99-99' : (h.effectiveTo || '9999-99-99');
        return options.historicalDate! >= from && options.historicalDate! <= to;
      });
      if (match) referenceLocation = match;
    }
    if (!referenceLocation && previousResidence.length > 0) {
      referenceLocation = previousResidence[previousResidence.length - 1];
    }
  }

  return {
    birthPlace: birthRecord,
    currentResidenceStatus,
    currentResidence,
    previousResidence,
    referenceLocation,
    selectedOrigin,
    selectedDestination,
    locationHistory
  };
}

// --------------------------------------------------------------------------
// Level-2 Forensic Helper 1: Parashari D-4 (Chaturthamsha) Calculation
// --------------------------------------------------------------------------

/**
 * Calculates genuine Parashari D-4 (Chaturthamsha) varga chart from planet longitudes
 * and validates its contribution to residence, land, fixed assets, and mobility.
 */
export function calculateParashariD4(
  kundali: CompleteKundali,
  fourthLordD1: PlanetName,
  twelfthLordD1: PlanetName,
  lang: Language = 'en'
): D4ChaturthamshaAnalysis {
  function getD4SignIndex(longitude: number): number {
    const rashi = Math.floor(longitude / 30);
    const degInSign = longitude % 30;
    const quarter = Math.floor(degInSign / 7.5);
    return (rashi + quarter * 3) % 12;
  }

  // Ascendant / Lagna
  let d4LagnaIndex = 0;
  if (kundali.charts?.D4?.ascendantIndex !== undefined) {
    d4LagnaIndex = kundali.charts.D4.ascendantIndex;
  } else {
    const ascPl = kundali.planets?.find(p => p.name === 'Ascendant');
    const ascLon = ascPl?.longitude ?? (kundali.houses?.[0]?.degreeStart ?? 0);
    d4LagnaIndex = getD4SignIndex(ascLon);
  }

  const d4LagnaSign = ZODIAC_SIGNS[d4LagnaIndex];

  // 4th house in D-4
  const d4FourthHouseIndex = (d4LagnaIndex + 3) % 12;
  const d4FourthHouseSign = ZODIAC_SIGNS[d4FourthHouseIndex];
  const d4FourthLord = SIGN_LORDS[d4FourthHouseSign];

  // Find placement of D1 4th lord in D-4
  const fourthLordPl = kundali.planets?.find(p => p.name === fourthLordD1);
  const fourthLordLon = fourthLordPl?.longitude ?? 0;
  const d4FourthLordSignIndex = getD4SignIndex(fourthLordLon);
  const d4FourthLordPlacementSign = ZODIAC_SIGNS[d4FourthLordSignIndex];
  const d4FourthLordPlacementHouse = ((d4FourthLordSignIndex - d4LagnaIndex + 12) % 12) + 1;

  // Find placement of D1 12th lord in D-4
  const twelfthLordPl = kundali.planets?.find(p => p.name === twelfthLordD1);
  const twelfthLordLon = twelfthLordPl?.longitude ?? 0;
  const d4TwelfthLordSignIndex = getD4SignIndex(twelfthLordLon);
  const d4TwelfthLordPlacementHouse = ((d4TwelfthLordSignIndex - d4LagnaIndex + 12) % 12) + 1;

  // Rahu in D-4
  const rahuPl = kundali.planets?.find(p => p.name === 'Rahu');
  const rahuLon = rahuPl?.longitude ?? 0;
  const d4RahuSignIndex = getD4SignIndex(rahuLon);
  const d4RahuPlacementHouse = ((d4RahuSignIndex - d4LagnaIndex + 12) % 12) + 1;

  // Astrological mobility scoring based on signs & houses:
  const movableIndices = [0, 3, 6, 9];
  const dualIndices = [2, 5, 8, 11];
  const fixedIndices = [1, 4, 7, 10];

  let mobilityScore = 50;

  if (movableIndices.includes(d4FourthHouseIndex)) {
    mobilityScore += 20;
  } else if (fixedIndices.includes(d4FourthHouseIndex)) {
    mobilityScore -= 18;
  } else if (dualIndices.includes(d4FourthHouseIndex)) {
    mobilityScore += 8;
  }

  if (movableIndices.includes(d4FourthLordSignIndex)) {
    mobilityScore += 16;
  } else if (fixedIndices.includes(d4FourthLordSignIndex)) {
    mobilityScore -= 12;
  }

  // House placements in D-4
  if ([3, 9, 12].includes(d4FourthLordPlacementHouse)) {
    mobilityScore += 24;
  } else if (d4FourthLordPlacementHouse === 4) {
    mobilityScore -= 20;
  }

  if ([4, 9, 12].includes(d4RahuPlacementHouse)) {
    mobilityScore += 18;
  }

  if ([4, 12].includes(d4TwelfthLordPlacementHouse)) {
    mobilityScore += 14;
  }

  const boundedScore = Math.max(15, Math.min(95, mobilityScore));
  const mobilityNature = boundedScore >= 60 ? 'HIGH_MOBILITY' : boundedScore >= 42 ? 'DUAL_RESIDENCE' : 'FIXED_DOMICILE';
  const supportive = mobilityNature !== 'FIXED_DOMICILE';

  const classicalTextBasisEn = `Parashari D-4 Chaturthamsha assigns 4th House to ${d4FourthHouseSign} (${movableIndices.includes(d4FourthHouseIndex) ? 'Chara/Movable sign favoring movement' : fixedIndices.includes(d4FourthHouseIndex) ? 'Sthira/Fixed sign favoring stability' : 'Dvisvabhava/Dual sign favoring dual residences'}). 4th Lord placed in House ${d4FourthLordPlacementHouse} of D-4.`;
  const classicalTextBasisHi = `बृहत्पाराशर होराशास्त्रानुसार चतुर्थांश (D-4) में चतुर्थ भाव ${d4FourthHouseSign} राशि में स्थित है। चतुर्थेश D-4 के ${d4FourthLordPlacementHouse}वें भाव में विराजमान होकर ${supportive ? 'स्थानांतरण का समर्थन' : 'स्थायी निवास का संकेत'} करता है।`;

  return {
    isCalculated: true,
    supportive,
    d4LagnaSign,
    d4FourthHouseSign,
    d4FourthLord,
    d4FourthLordPlacementSign,
    d4FourthLordPlacementHouse,
    d4MobilityScore: boundedScore,
    mobilityNature,
    classicalTextBasisEn,
    classicalTextBasisHi
  };
}

// --------------------------------------------------------------------------
// Level-2 Forensic Helper 2: Dynamic Planetary Peak Calculation
// --------------------------------------------------------------------------

export function calculateAstronomicalPeak(
  start: Date,
  end: Date,
  pdPlanet: PlanetName,
  adPlanet: PlanetName,
  lang: Language = 'en'
): PeakDateDetails {
  const startMs = start.getTime();
  const endMs = end.getTime();
  const totalMs = Math.max(86400000, endMs - startMs);

  let ratio = 0.50;
  let velocityNature = 'Saumya / Benefic steady crescendo (culminating at sub-period midpoint)';

  switch (pdPlanet) {
    case 'Saturn':
    case 'Rahu':
      ratio = 0.65;
      velocityNature = 'Manda / Slow-maturing karmic culmination after foundational testing';
      break;
    case 'Mars':
    case 'Sun':
      ratio = 0.35;
      velocityNature = 'Tejas / Swift initiating ingress with rapid event crystallization';
      break;
    case 'Mercury':
      ratio = 0.45;
      velocityNature = 'Chara / Rapid adaptable inflection and logistical mobilization';
      break;
    case 'Ketu':
      ratio = 0.38;
      velocityNature = 'Akash / Abrupt catalytic shift and detachment from prior mooring';
      break;
    case 'Jupiter':
    case 'Venus':
    case 'Moon':
    default:
      ratio = 0.50;
      velocityNature = 'Saumya / Harmonious crescendo at astronomical sub-period midpoint';
      break;
  }

  const peakMs = startMs + Math.round(totalMs * ratio);
  const peakDateObj = new Date(peakMs);
  const peakDateFormatted = formatIsoDate(peakDateObj, lang);
  const peakDateIso = peakDateObj.toISOString().split('T')[0];

  const peakMethodologyEn = `Peak resonance calculated at ${Math.round(ratio * 100)}% of the ${pdPlanet} Pratyantardasha window based on ${velocityNature}.`;
  const peakMethodologyHi = `शीर्ष प्रभाव बिंदु ${pdPlanet} प्रत्यन्तर्दशा के ${Math.round(ratio * 100)}% भाग पर निर्धारित (${velocityNature})।`;

  return {
    peakDate: peakDateFormatted,
    peakDateIso,
    culminationRatio: ratio,
    governingPlanet: pdPlanet,
    planetaryVelocityNature: velocityNature,
    peakMethodologyEn,
    peakMethodologyHi
  };
}

// --------------------------------------------------------------------------
// Level-2 Forensic Helper 3: Calendar Duration Arithmetic & Open-Ended Transitions
// --------------------------------------------------------------------------

export function calculateRelocationDuration(
  start: Date,
  end: Date,
  isSettlement: boolean,
  d4MobilityScore: number,
  lang: Language = 'en'
): RelocationDurationDetails {
  const durationMs = Math.max(86400000, end.getTime() - start.getTime());
  const durationDays = Math.round(durationMs / 86400000);
  const durationMonths = Math.max(1, Math.round(durationDays / 30.4375));
  const durationYears = Number((durationDays / 365.2425).toFixed(1));

  let durationClass: DurationClass = 'SHORT_TERM';
  let isOpenEnded = false;

  if (isSettlement || (durationDays > 730 && d4MobilityScore >= 68)) {
    durationClass = 'SETTLEMENT_ORIENTED';
    isOpenEnded = true;
  } else if (durationDays > 730) {
    durationClass = 'LONG_TERM';
    isOpenEnded = false;
  } else if (durationDays >= 180) {
    durationClass = 'MEDIUM_TERM';
    isOpenEnded = false;
  } else {
    durationClass = 'SHORT_TERM';
    isOpenEnded = false;
  }

  let durationDisplayEn = '';
  let durationDisplayHi = '';

  if (isOpenEnded) {
    durationDisplayEn = `Long-term / Open-ended transition (~${durationYears} years initial window, continuing residence establishment)`;
    durationDisplayHi = `दीर्घकालिक / स्थायी आवास स्थापना (~${durationYears} वर्ष प्रारंभिक अवधि, निरंतर निवास)`;
  } else if (durationDays > 730) {
    durationDisplayEn = `~${durationYears} years (${durationDays} days) — Extended Multi-Year Relocation`;
    durationDisplayHi = `~${durationYears} वर्ष (${durationDays} दिन) — बहु-वर्षीय विस्तारित स्थानांतरण`;
  } else if (durationDays >= 180) {
    durationDisplayEn = `~${durationMonths} months (${durationDays} days) — Medium-Term Assignment`;
    durationDisplayHi = `~${durationMonths} माह (${durationDays} दिन) — मध्यम-अवधि कार्य प्रवास`;
  } else {
    const weeks = Math.max(1, Math.round(durationDays / 7));
    durationDisplayEn = `~${weeks} weeks (${durationDays} days) — Temporary / Project-Based Movement`;
    durationDisplayHi = `~${weeks} सप्ताह (${durationDays} दिन) — अल्पकालिक / परियोजना-आधारित यात्रा`;
  }

  const derivationMethodology = `Calculated using calendar-safe date arithmetic (${durationDays} solar days) mapped to classical Dasha duration spans.`;

  return {
    durationDays,
    durationMonths,
    durationYears,
    durationDisplayEn,
    durationDisplayHi,
    durationClass,
    isOpenEnded,
    openEndedNoteEn: isOpenEnded ? 'Event signature initiates lasting change of domicile beyond single transit window.' : undefined,
    openEndedNoteHi: isOpenEnded ? 'यह योग एकल गोचर से परे एक स्थायी अथवा दीर्घकालिक गृह-स्थापना का सूत्रपात करता है।' : undefined,
    derivationMethodology
  };
}

// --------------------------------------------------------------------------
// Level-2 Forensic Helper 4: Career + Residence Overlap Modeling
// --------------------------------------------------------------------------

export function calculateCareerResidenceOverlap(
  isFourthActive: boolean,
  isTenthActive: boolean,
  dasha: { maha: PlanetName; antar: PlanetName; pratyantar: PlanetName },
  tenthLord: PlanetName,
  fourthLord: PlanetName,
  startDate: string,
  endDate: string,
  lang: Language = 'en'
): CareerResidenceOverlap {
  const isCoupled = isFourthActive && isTenthActive;
  let overlapType: 'SIMULTANEOUS_DUAL' | 'CAREER_THEN_RELOCATION' | 'RELOCATION_THEN_CAREER' | 'INDEPENDENT_WINDOW' = 'INDEPENDENT_WINDOW';
  let lagMonths = 0;

  if (isCoupled) {
    if (dasha.maha === tenthLord || dasha.antar === tenthLord) {
      overlapType = 'CAREER_THEN_RELOCATION';
      lagMonths = 2;
    } else if (dasha.maha === fourthLord || dasha.antar === fourthLord) {
      overlapType = 'RELOCATION_THEN_CAREER';
      lagMonths = 1;
    } else {
      overlapType = 'SIMULTANEOUS_DUAL';
      lagMonths = 0;
    }
  } else if (isTenthActive) {
    overlapType = 'INDEPENDENT_WINDOW';
  } else if (isFourthActive) {
    overlapType = 'INDEPENDENT_WINDOW';
  }

  const careerWindowEn = isTenthActive
    ? `Active throughout ${startDate} to ${endDate} under ${tenthLord} influence.`
    : 'Career axis inactive during this window.';
  const careerWindowHi = isTenthActive
    ? `${tenthLord} के प्रभाव में ${startDate} से ${endDate} तक करियर अक्ष सक्रिय।`
    : 'इस कालखंड में आजीविका अक्ष निष्क्रिय।';

  const residenceWindowEn = isFourthActive
    ? `Physical relocation window aligned with ${fourthLord} sub-cycle.`
    : 'Residence axis inactive.';
  const residenceWindowHi = isFourthActive
    ? `${fourthLord} के उप-चक्र में निवास परिवर्तन कालखंड संरेखित।`
    : 'निवास परिवर्तन अक्ष निष्क्रिय।';

  const synthesisEn = overlapType === 'SIMULTANEOUS_DUAL'
    ? 'Simultaneous dual convergence: professional duties and household relocation execute synchronously.'
    : overlapType === 'CAREER_THEN_RELOCATION'
    ? `Career catalyst leads: new professional responsibilities initiate first, followed by physical residential shifting with an estimated ~${lagMonths} months operational lag.`
    : overlapType === 'RELOCATION_THEN_CAREER'
    ? 'Domestic transfer precedes professional settling: household established before new career phase reaches full stride.'
    : 'Independent temporal window: focus remains primarily on one dimensional vector.';

  const synthesisHi = overlapType === 'SIMULTANEOUS_DUAL'
    ? 'समकालिक द्वैध अभिसरण: व्यावसायिक दायित्व एवं गृह स्थानांतरण एक साथ संपन्न।'
    : overlapType === 'CAREER_THEN_RELOCATION'
    ? `कार्य-प्रेरित प्राथमिक कदम: नवीन व्यावसायिक भूमिका पहले प्रारंभ, तदुपरांत लगभग ~${lagMonths} माह में भौतिक निवास परिवर्तन।`
    : overlapType === 'RELOCATION_THEN_CAREER'
    ? 'निवास स्थापना पहले: नए कार्य-क्षेत्र के पूर्ण विस्तार से पूर्व पारिवारिक गृह स्थापना।'
    : 'स्वतंत्र कालखंड: मुख्य ध्यान एक ही प्रमुख आयाम पर केंद्रित।';

  return {
    careerResidenceCoupling: isCoupled,
    overlapType,
    careerWindowEn,
    careerWindowHi,
    residenceWindowEn,
    residenceWindowHi,
    temporalLagMonths: lagMonths,
    synthesisEn,
    synthesisHi
  };
}

// --------------------------------------------------------------------------
// Level-2 Forensic Helper 5: India vs Outside India Classification
// --------------------------------------------------------------------------

export function classifyGeographicContext(
  origin: string | null,
  destination: string | null,
  foreignRelocationScore: number,
  foreignTravelScore: number
): GeographicContextClassification {
  const originStr = (origin || '').toLowerCase();
  const destStr = (destination || '').toLowerCase();

  const indianKeywords = [
    'india', 'bharat', 'delhi', 'mumbai', 'bombay', 'bangalore', 'bengaluru', 'chennai', 'madras',
    'kolkata', 'calcutta', 'hyderabad', 'pune', 'ambala', 'haryana', 'punjab', 'rajasthan', 'gujarat',
    'maharashtra', 'karnataka', 'tamil nadu', 'kerala', 'uttar pradesh', 'bihar', 'noida', 'gurugram',
    'gurgaon', 'chandigarh', 'ahmedabad', 'jaipur', 'lucknow', 'indore', 'bhopal', 'kochi', 'goa', 'shimla'
  ];

  const isOriginIndia = indianKeywords.some(k => originStr.includes(k));
  const isDestIndia = indianKeywords.some(k => destStr.includes(k));

  let originJurisdiction: 'INDIA_DOMESTIC' | 'INTERNATIONAL' | 'UNSPECIFIED' = 'UNSPECIFIED';
  if (origin) {
    originJurisdiction = isOriginIndia ? 'INDIA_DOMESTIC' : 'INTERNATIONAL';
  }

  let destinationJurisdiction: 'INDIA_DOMESTIC' | 'INTERNATIONAL' | 'UNSPECIFIED' = 'UNSPECIFIED';
  if (destination) {
    destinationJurisdiction = isDestIndia ? 'INDIA_DOMESTIC' : 'INTERNATIONAL';
  } else if (foreignRelocationScore >= 60 || foreignTravelScore >= 75) {
    destinationJurisdiction = 'INTERNATIONAL';
  } else if (originJurisdiction === 'INDIA_DOMESTIC') {
    destinationJurisdiction = 'INDIA_DOMESTIC';
  }

  const borderCrossing = (originJurisdiction === 'INDIA_DOMESTIC' && destinationJurisdiction === 'INTERNATIONAL') ||
                         (originJurisdiction === 'INTERNATIONAL' && destinationJurisdiction === 'INDIA_DOMESTIC');

  const jurisdictionExplanationEn = originJurisdiction === 'INDIA_DOMESTIC'
    ? borderCrossing
      ? 'Cross-border movement originating within India and transitioning to an overseas/international jurisdiction.'
      : 'Domestic intra-national movement within India (inter-state or inter-city transfer).'
    : originJurisdiction === 'INTERNATIONAL'
    ? borderCrossing
      ? 'Cross-border return or relocation from an international residence back to India.'
      : 'International relocation between foreign jurisdictions.'
    : 'Geographic jurisdiction will calibrate dynamically upon entering reference origin.';

  const jurisdictionExplanationHi = originJurisdiction === 'INDIA_DOMESTIC'
    ? borderCrossing
      ? 'भारत से विदेश की सीमा-पार यात्रा अथवा अंतरराष्ट्रीय स्थानांतरण।'
      : 'भारत के भीतर घरेलू अंतर-राज्यीय अथवा अंतर-नगरीय स्थानांतरण।'
    : originJurisdiction === 'INTERNATIONAL'
    ? borderCrossing
      ? 'अंतरराष्ट्रीय निवास से भारत वापसी अथवा स्थानांतरण।'
      : 'विदेशी क्षेत्राधिकारों के मध्य अंतरराष्ट्रीय स्थानांतरण।'
    : 'संदर्भ स्थान दर्ज होने पर क्षेत्राधिकार स्वतः निर्धारित होगा।';

  return {
    originJurisdiction,
    destinationJurisdiction,
    borderCrossing,
    jurisdictionExplanationEn,
    jurisdictionExplanationHi
  };
}

// --------------------------------------------------------------------------
// Level-2 Forensic Helper 6: Direction Provenance & Sanskrit Attribution
// --------------------------------------------------------------------------

export function deriveDirectionProvenance(
  direction: CardinalDirection,
  referenceOriginValue: string | null,
  twelfthSign: string,
  ninthLord: PlanetName,
  lang: Language = 'en'
): DirectionProvenanceDetails {
  const rashiBasis = `12th Bhava Sign (${twelfthSign}) elemental governance (${SIGN_DIRECTIONS[twelfthSign] || 'NORTH'})`;
  const grahaBasis = `9th Lord (${ninthLord}) Digbala orientation vector (${GRAHA_DIRECTIONS[ninthLord] || 'NORTH_EAST'})`;
  const originLabel = referenceOriginValue ? referenceOriginValue : 'Unspecified Reference Origin';

  const classicalSourceEn = 'Brihat Parashara Hora Shastra Ch. 4 (Rashi Lakshana & Dik Nirdesha): Aries/Leo/Sagittarius (East), Taurus/Virgo/Capricorn (South), Gemini/Libra/Aquarius (West), Cancer/Scorpio/Pisces (North). Direction evaluated strictly from confirmed geographic reference origin.';
  const classicalSourceHi = 'बृहत्पाराशर होराशास्त्र अध्याय ४ (राशि लक्षण एवं दिक् निर्देश): मेष/सिंह/धनु (पूर्व), वृषभ/कन्या/मकर (दक्षिण), मिथुन/तुला/कुंभ (पश्चिम), कर्क/वृश्चिक/मीन (उत्तर)। दिशा का निर्धारण पुष्ट भौगोलिक संदर्भ मूल स्थान से किया गया है।';

  return {
    direction,
    referenceOriginLabel: originLabel,
    rashiElementBasis: rashiBasis,
    grahaDigbalaBasis: grahaBasis,
    classicalSourceEn,
    classicalSourceHi
  };
}

// --------------------------------------------------------------------------
// Level-2 Forensic Helper 7: Transparent 5-Part Weighted Evidence Matrix
// --------------------------------------------------------------------------

export function calculateEvidenceBreakdown(
  isFourthActive: boolean,
  isTenthActive: boolean,
  isTwelfthActive: boolean,
  isNinthActive: boolean,
  dashaResonanceScore: number,
  b4: number,
  b12: number,
  b9: number,
  d4MobilityScore: number,
  gocharTransitScore: number
): { items: RelocationEvidenceItem[]; confidenceScore: number; evidenceStrength: 'STRONG' | 'MODERATE' | 'EMERGING' | 'INSUFFICIENT' } {
  let bhavaRaw = 45;
  if (isFourthActive) bhavaRaw += 20;
  if (isTenthActive) bhavaRaw += 15;
  if (isTwelfthActive) bhavaRaw += 15;
  if (isNinthActive) bhavaRaw += 5;
  bhavaRaw = Math.min(100, bhavaRaw);

  const dashaRaw = Math.min(100, Math.max(30, dashaResonanceScore));
  const savRaw = Math.min(100, Math.max(20, Math.round(50 + (28 - b4) * 2.2 + (b12 - 25) * 2.0 + (b9 - 26) * 1.5)));
  const d4Raw = d4MobilityScore;
  const gocharRaw = Math.min(100, Math.max(35, gocharTransitScore));

  const items: RelocationEvidenceItem[] = [
    {
      dimension: 'D1 Natal Bhava & Bhavesh Alignment',
      weight: 25,
      rawScore: bhavaRaw,
      weightedScore: Math.round(bhavaRaw * 0.25),
      classicalBasisEn: 'Parashari activation of 4th (residence), 10th (karma/career), and 12th (remote environment) lords.',
      classicalBasisHi: 'चतुर्थ (गृह), दशम (आजीविका) एवं द्वादश (दूरस्थ स्थान) भाव स्वामियों का संबंध।'
    },
    {
      dimension: 'Vimshottari Dasha Hierarchy',
      weight: 25,
      rawScore: dashaRaw,
      weightedScore: Math.round(dashaRaw * 0.25),
      classicalBasisEn: 'Concordance between Mahadasha, Antardasha, and Pratyantardasha rulers.',
      classicalBasisHi: 'महादशा, अन्तर्दशा एवं प्रत्यन्तर्दशा स्वामियों का अनुकूल संयोग।'
    },
    {
      dimension: 'Ashtakavarga Bindu Distribution',
      weight: 20,
      rawScore: savRaw,
      weightedScore: Math.round(savRaw * 0.20),
      classicalBasisEn: `4th house bindus (${b4}/28 threshold) indicating domestic inertia vs 12th house bindus (${b12}) mobility fruit.`,
      classicalBasisHi: `चतुर्थ भाव बिन्दु (${b4}/२८) स्थिरता एवं द्वादश भाव बिन्दु (${b12}) प्रवास फल का संतुलन।`
    },
    {
      dimension: 'D-4 Chaturthamsha Confirmation',
      weight: 15,
      rawScore: d4Raw,
      weightedScore: Math.round(d4Raw * 0.15),
      classicalBasisEn: 'Parashari D-4 varga evaluating fixed assets, movable rashis, and domicile displacement.',
      classicalBasisHi: 'चतुर्थांश (D-4) वर्ग चक्र द्वारा अचल संपत्ति एवं निवास गतिशीलता का परीक्षण।'
    },
    {
      dimension: 'Gochar Transit Convergence',
      weight: 15,
      rawScore: gocharRaw,
      weightedScore: Math.round(gocharRaw * 0.15),
      classicalBasisEn: 'Dual transit activation of major planets (Saturn/Jupiter/Rahu) over natal axes.',
      classicalBasisHi: 'प्रमुख ग्रहों (शनि/गुरु/राहु) का जन्मकालीन अक्षों पर द्वैध गोचर प्रभाव।'
    }
  ];

  const totalWeighted = items.reduce((sum, item) => sum + item.weightedScore, 0);
  const confidenceScore = Math.max(50, Math.min(96, totalWeighted));
  const evidenceStrength = confidenceScore >= 80 ? 'STRONG' : confidenceScore >= 65 ? 'MODERATE' : confidenceScore >= 50 ? 'EMERGING' : 'INSUFFICIENT';

  return { items, confidenceScore, evidenceStrength };
}

// --------------------------------------------------------------------------
// Core Timing Window Computation for Primary Event
// --------------------------------------------------------------------------

interface CalculatedTimingWindow {
  startDate: string;
  endDate: string;
  peakDate: string;
  startDateObj: Date;
  endDateObj: Date;
  peakDateObj: Date;
  currentStatus: RelocationCurrentStatus;
  activeMahadasha: PlanetName;
  activeAntardasha: PlanetName;
  activePratyantardasha: PlanetName;
  activeResonanceHouse: number;
  dashaResonanceScore: number;
  peakDetails: PeakDateDetails;
}

function calculateDynamicRelocationWindow(
  kundali: CompleteKundali,
  focusDate: Date,
  relevantLords: PlanetName[],
  lang: Language
): CalculatedTimingWindow {
  const focusMs = focusDate.getTime();
  const dashas = kundali.dashas || [];

  interface CandidatePD {
    mdPlanet: PlanetName;
    adPlanet: PlanetName;
    pdPlanet: PlanetName;
    start: Date;
    end: Date;
    resonanceScore: number;
    house: number;
  }

  const candidateWindows: CandidatePD[] = [];

  for (const md of dashas) {
    const mdPlanet = md.planet as PlanetName;
    if (!md.antardashas) continue;

    for (const ad of md.antardashas) {
      const adPlanet = ad.planet as PlanetName;
      const adStart = new Date(ad.startDate);
      const adEnd = new Date(ad.endDate);
      const totalAdMs = adEnd.getTime() - adStart.getTime();

      const adIdx = DASHA_CYCLE_ORDER.findIndex(d => d.planet === adPlanet);
      if (adIdx < 0) continue;

      let cur = new Date(adStart.getTime());

      // Generate the 9 Pratyantardashas
      for (let k = 0; k < 9; k++) {
        const pdObj = DASHA_CYCLE_ORDER[(adIdx + k) % 9];
        const frac = pdObj.years / 120;
        const durMs = totalAdMs * frac;
        const next = new Date(cur.getTime() + durMs);

        let resScore = 0;
        const resonantHouse = 4;

        if (relevantLords.includes(pdObj.planet)) {
          resScore += 50;
        }
        if (relevantLords.includes(adPlanet)) {
          resScore += 35;
        }
        if (relevantLords.includes(mdPlanet)) {
          resScore += 20;
        }

        if (['Rahu', 'Moon', 'Saturn', 'Jupiter'].includes(pdObj.planet)) {
          resScore += 15;
        }

        const timeDiff = Math.abs(cur.getTime() - focusMs);
        if (timeDiff < 10 * 365.25 * 86400000) {
          candidateWindows.push({
            mdPlanet,
            adPlanet,
            pdPlanet: pdObj.planet,
            start: cur,
            end: next,
            resonanceScore: resScore - Math.floor(timeDiff / (60 * 86400000)),
            house: resonantHouse
          });
        }

        cur = next;
      }
    }
  }

  const activeNowCandidate = candidateWindows.find(
    c => focusMs >= c.start.getTime() && focusMs <= c.end.getTime()
  );

  let chosen: CandidatePD;

  if (activeNowCandidate && activeNowCandidate.resonanceScore >= 20) {
    chosen = activeNowCandidate;
  } else {
    const upcomingCandidates = candidateWindows
      .filter(c => c.end.getTime() >= focusMs)
      .sort((a, b) => b.resonanceScore - a.resonanceScore);

    if (upcomingCandidates.length > 0) {
      chosen = upcomingCandidates[0];
    } else if (candidateWindows.length > 0) {
      chosen = candidateWindows[candidateWindows.length - 1];
    } else {
      const fallbackStart = new Date(focusMs + 60 * 86400000);
      const fallbackEnd = new Date(focusMs + 240 * 86400000);
      chosen = {
        mdPlanet: 'Jupiter',
        adPlanet: 'Saturn',
        pdPlanet: 'Mercury',
        start: fallbackStart,
        end: fallbackEnd,
        resonanceScore: 60,
        house: 4
      };
    }
  }

  let currentStatus: RelocationCurrentStatus = 'UPCOMING';
  if (focusMs >= chosen.start.getTime() && focusMs <= chosen.end.getTime()) {
    currentStatus = 'ACTIVE_NOW';
  } else if (focusMs > chosen.end.getTime()) {
    currentStatus = 'ENDED';
  }

  const peakDetails = calculateAstronomicalPeak(chosen.start, chosen.end, chosen.pdPlanet, chosen.adPlanet, lang);
  const peakDateObj = new Date(peakDetails.peakDateIso);

  return {
    startDate: formatIsoDate(chosen.start, lang),
    endDate: formatIsoDate(chosen.end, lang),
    peakDate: peakDetails.peakDate,
    startDateObj: chosen.start,
    endDateObj: chosen.end,
    peakDateObj,
    currentStatus,
    activeMahadasha: chosen.mdPlanet,
    activeAntardasha: chosen.adPlanet,
    activePratyantardasha: chosen.pdPlanet,
    activeResonanceHouse: chosen.house,
    dashaResonanceScore: chosen.resonanceScore,
    peakDetails
  };
}

// --------------------------------------------------------------------------
// RG-HORIZON-EXT-V6.0 Multi-Event Discovery Engine
// --------------------------------------------------------------------------

/**
 * Discovers distinct relocation event windows across the selected horizon [focusDate, horizonEndDate].
 * Enforces adaptive temporal resolution:
 * 7D: Prāṇa + Sūkṣma + fast Gochar (1 short event)
 * 30D: Sūkṣma + PD + Chandra (near-term immediate transitions)
 * 90D / 6M: PD + AD (1–2 distinct windows)
 * 1Y / 3Y / 5Y / 10Y: MD + AD phases + major Gochar triggers (multiple distinct windows)
 */
export function discoverHorizonRelocationWindows(
  kundali: CompleteKundali,
  focusDate: Date,
  horizon: RelocationTimeHorizon,
  primaryTiming: CalculatedTimingWindow,
  primaryEvent: {
    eventType: string;
    primaryDriver: RelocationDriver;
    direction: CardinalDirection;
    directionReference: DirectionReferencePoint;
    directionReferenceLabelEn: string;
    directionReferenceLabelHi: string;
    geographicScale: GeographicScale;
    referenceLocationValue: string | null;
    directionAvailable: boolean;
    confidenceScore: number;
    evidenceStrength: 'STRONG' | 'MODERATE' | 'EMERGING' | 'INSUFFICIENT';
    b4: number;
    b12: number;
    d4Analysis: D4ChaturthamshaAnalysis;
    isIndianOrigin: boolean;
  },
  lang: Language = 'en'
): RelocationHorizonDiscovery {
  const isHi = lang === 'hi';
  const focusMs = focusDate.getTime();
  const horizonEnd = calculateHorizonEndDate(focusDate, horizon);
  const horizonEndMs = horizonEnd.getTime();
  const discoveryIntervalDays = Math.round((horizonEndMs - focusMs) / 86400000);

  const events: RelocationEventWindow[] = [];
  const dashas = kundali.dashas || [];

  // Precision configuration per horizon
  let precisionLevel: 'DAY_EXACT' | 'WEEK_EXACT' | 'MONTH_RANGE' | 'SEASON_APPROXIMATE' | 'MULTI_YEAR_PHASE' = 'MONTH_RANGE';
  let precisionDisplayEn = 'Month-level precision';
  let precisionDisplayHi = 'मासिक परिशुद्धता';

  switch (horizon) {
    case '7D':
      precisionLevel = 'DAY_EXACT';
      precisionDisplayEn = 'Day-exact precision (immediate transit)';
      precisionDisplayHi = 'दैनिक परिशुद्धता (तात्कालिक गोचर)';
      break;
    case '30D':
      precisionLevel = 'WEEK_EXACT';
      precisionDisplayEn = 'Week-exact precision (near-term sub-cycle)';
      precisionDisplayHi = 'साप्ताहिक परिशुद्धता (निकटवर्ती चक्र)';
      break;
    case '90D':
    case '6M':
    case '1Y':
      precisionLevel = 'MONTH_RANGE';
      precisionDisplayEn = 'Month-range precision (Antardasha maturation)';
      precisionDisplayHi = 'मासिक कालखंड परिशुद्धता (अन्तर्दशा परिपक्वता)';
      break;
    case '3Y':
    case '5Y':
      precisionLevel = 'SEASON_APPROXIMATE';
      precisionDisplayEn = 'Season/quarter-approximate precision (multi-year transition)';
      precisionDisplayHi = 'ऋतु / त्रैमासिक अनुमानित परिशुद्धता (बहु-वर्षीय कालखंड)';
      break;
    case '10Y':
      precisionLevel = 'MULTI_YEAR_PHASE';
      precisionDisplayEn = 'Major Dasha phase precision (long-range structural window)';
      precisionDisplayHi = 'महादशा चरण परिशुद्धता (दीर्घकालिक संरचनात्मक कालखंड)';
      break;
  }

  // 1. Anchor Primary Event (Ensures 100% consistency across all horizons)
  const primaryStart = primaryTiming.startDateObj;
  const primaryEnd = primaryTiming.endDateObj;
  const primaryDurationMs = Math.max(86400000, primaryEnd.getTime() - primaryStart.getTime());
  const primaryDurationDays = Math.round(primaryDurationMs / 86400000);
  const primaryDurationMonths = Math.max(1, Math.round(primaryDurationDays / 30.4375));
  const primaryDurationYears = Number((primaryDurationDays / 365.2425).toFixed(1));

  let primaryGeogClass: 'WITHIN_INDIA' | 'OUTSIDE_INDIA' | 'UNDETERMINED' = 'UNDETERMINED';
  if (primaryEvent.directionAvailable) {
    primaryGeogClass = primaryEvent.geographicScale === 'FOREIGN_OVERSEAS'
      ? 'OUTSIDE_INDIA'
      : primaryEvent.isIndianOrigin ? 'WITHIN_INDIA' : 'OUTSIDE_INDIA';
  }

  // Include primary event if it overlaps with the horizon window
  if (primaryEnd.getTime() >= focusMs && primaryStart.getTime() <= horizonEndMs) {
    events.push({
      eventId: 'reloc_evt_primary_anchor',
      startDate: primaryTiming.startDate,
      endDate: primaryTiming.endDate,
      peakDate: primaryTiming.peakDate,
      startDateIso: primaryStart.toISOString().split('T')[0],
      endDateIso: primaryEnd.toISOString().split('T')[0],
      peakDateIso: primaryTiming.peakDateObj.toISOString().split('T')[0],
      durationDisplay: primaryDurationDays > 730
        ? `~${primaryDurationYears} years (${primaryDurationDays} days)`
        : `~${primaryDurationMonths} months (${primaryDurationDays} days)`,
      durationDays: primaryDurationDays,
      durationMonths: primaryDurationMonths,
      durationYears: primaryDurationYears,
      isOpenEnded: primaryDurationDays > 730 && primaryEvent.d4Analysis.d4MobilityScore >= 68,
      lifecycle: focusMs >= primaryStart.getTime() && focusMs <= primaryEnd.getTime()
        ? 'ACTIVE'
        : focusMs < primaryStart.getTime() ? 'UPCOMING' : 'CONCLUDED',
      temporalPhase: focusMs > primaryEnd.getTime()
        ? 'ATITA'
        : focusMs >= primaryStart.getTime() ? 'VARTAMANA' : 'AGAMI',
      eventType: primaryEvent.eventType as any,
      primaryDriver: primaryEvent.primaryDriver,
      direction: primaryEvent.direction,
      directionReference: primaryEvent.directionReference,
      directionReferenceLabelEn: primaryEvent.directionReferenceLabelEn,
      directionReferenceLabelHi: primaryEvent.directionReferenceLabelHi,
      geographicScale: primaryEvent.geographicScale,
      geographicClassification: primaryGeogClass,
      referenceLocation: primaryEvent.referenceLocationValue,
      evidenceStrength: primaryEvent.evidenceStrength,
      confidenceScore: primaryEvent.confidenceScore,
      btrSensitivity: 'STABLE',
      precisionLevel,
      precisionDisplayEn,
      precisionDisplayHi,
      overlapDetails: primaryEvent.eventType.includes('Career + Residence') ? {
        careerStart: primaryTiming.startDate,
        careerEnd: primaryTiming.endDate,
        residenceStart: primaryTiming.startDate,
        residenceEnd: primaryTiming.endDate,
        overlapStart: primaryTiming.startDate,
        overlapEnd: primaryTiming.endDate,
        overlapDurationDays: primaryDurationDays
      } : undefined
    });
  }

  // 2. Discover Additional Future Event Windows for Horizons > 30D (90D, 6M, 1Y, 3Y, 5Y, 10Y)
  if (['90D', '6M', '1Y', '3Y', '5Y', '10Y'].includes(horizon)) {
    // Scan Dasha periods that occur strictly AFTER primary event's end, within horizonEndMs
    let windowCounter = 1;

    for (const md of dashas) {
      if (!md.antardashas) continue;
      const mdPlanet = md.planet as PlanetName;

      for (const ad of md.antardashas) {
        const adPlanet = ad.planet as PlanetName;
        const adStart = new Date(ad.startDate);
        const adEnd = new Date(ad.endDate);

        // Check if this AD window falls inside discovery window after primary event
        if (adStart.getTime() > primaryEnd.getTime() && adStart.getTime() <= horizonEndMs) {
          // Astrological filter: Only retain periods with genuine relocation or career triggers
          // 4th/10th/12th/9th lords or Rahu/Saturn/Jupiter
          const hasRelocTrigger = ['Rahu', 'Jupiter', 'Saturn', 'Moon', 'Mercury'].includes(adPlanet) ||
                                  ['Rahu', 'Jupiter', 'Saturn'].includes(mdPlanet);

          if (hasRelocTrigger) {
            windowCounter++;
            const evtId = `reloc_evt_${horizon.toLowerCase()}_win_${windowCounter}`;
            const durMs = Math.max(86400000, adEnd.getTime() - adStart.getTime());
            const durDays = Math.round(durMs / 86400000);
            const durMonths = Math.max(1, Math.round(durDays / 30.4375));
            const durYears = Number((durDays / 365.2425).toFixed(1));

            // Peak calculation for this future window
            const peakMs = adStart.getTime() + Math.round(durMs * 0.50);
            const peakDateObj = new Date(peakMs);

            // BTR sensitivity: deeper into future (3Y, 5Y, 10Y) -> higher sensitivity
            const btrSensitivity = horizon === '10Y'
              ? 'MODERATELY_SENSITIVE'
              : horizon === '5Y' || horizon === '3Y' ? 'MODERATELY_SENSITIVE' : 'STABLE';

            // Determine future event type
            const isCareerLinked = ['Sun', 'Mars', 'Mercury'].includes(adPlanet);
            const evtType = isCareerLinked ? 'Career Relocation' : 'Residence Transition';

            // Geographic classification for future window
            let geogClass: 'WITHIN_INDIA' | 'OUTSIDE_INDIA' | 'UNDETERMINED' = 'UNDETERMINED';
            if (primaryEvent.directionAvailable) {
              geogClass = ['Rahu', 'Saturn'].includes(adPlanet) ? 'OUTSIDE_INDIA' : 'WITHIN_INDIA';
            }

            events.push({
              eventId: evtId,
              startDate: formatIsoDate(adStart, lang),
              endDate: formatIsoDate(adEnd, lang),
              peakDate: formatIsoDate(peakDateObj, lang),
              startDateIso: adStart.toISOString().split('T')[0],
              endDateIso: adEnd.toISOString().split('T')[0],
              peakDateIso: peakDateObj.toISOString().split('T')[0],
              durationDisplay: durDays > 730
                ? `~${durYears} years (${durDays} days)`
                : `~${durMonths} months (${durDays} days)`,
              durationDays: durDays,
              durationMonths: durMonths,
              durationYears: durYears,
              isOpenEnded: false,
              lifecycle: 'UPCOMING',
              temporalPhase: 'AGAMI',
              eventType: evtType,
              primaryDriver: isCareerLinked ? 'CAREER_PROFESSION' : 'FAMILY_HOME',
              direction: primaryEvent.direction,
              directionReference: primaryEvent.directionReference,
              directionReferenceLabelEn: primaryEvent.directionReferenceLabelEn,
              directionReferenceLabelHi: primaryEvent.directionReferenceLabelHi,
              geographicScale: geogClass === 'OUTSIDE_INDIA' ? 'FOREIGN_OVERSEAS' : 'LONG_DISTANCE',
              geographicClassification: geogClass,
              referenceLocation: primaryEvent.referenceLocationValue,
              evidenceStrength: 'MODERATE',
              confidenceScore: Math.round(primaryEvent.confidenceScore * 0.9),
              btrSensitivity,
              precisionLevel,
              precisionDisplayEn,
              precisionDisplayHi
            });

            // Prevent overloading: 1Y max 3, 3Y max 5, 10Y max 7
            const maxEvents = horizon === '1Y' ? 3 : horizon === '3Y' ? 5 : 7;
            if (events.length >= maxEvents) break;
          }
        }
      }
      if (events.length >= (horizon === '1Y' ? 3 : horizon === '3Y' ? 5 : 7)) break;
    }
  }

  // Summary counts
  const careerLinked = events.filter(e => e.eventType.includes('Career') || e.primaryDriver === 'CAREER_PROFESSION').length;
  const residenceOnly = events.filter(e => e.eventType === 'Residence Transition' && e.primaryDriver !== 'CAREER_PROFESSION').length;
  const internationalCount = events.filter(e => e.geographicClassification === 'OUTSIDE_INDIA').length;
  const domesticCount = events.filter(e => e.geographicClassification === 'WITHIN_INDIA').length;
  const directionEstablishedCount = events.filter(e => e.direction !== 'NOT_ESTABLISHED').length;

  const hasSignificantEvents = events.length > 0;
  const noEventMessageEn = hasSignificantEvents ? undefined : 'No significant relocation transition is established within this horizon.';
  const noEventMessageHi = hasSignificantEvents ? undefined : 'इस कालखंड में कोई महत्वपूर्ण स्थानांतरण या स्थान-परिवर्तन योग स्थापित नहीं है।';

  return {
    horizon,
    focusDate: formatIsoDate(focusDate, lang),
    horizonEndDate: formatIsoDate(horizonEnd, lang),
    discoveryIntervalDays,
    totalEventsDiscovered: events.length,
    hasSignificantEvents,
    noEventMessageEn,
    noEventMessageHi,
    summary: {
      totalWindows: events.length,
      careerLinked,
      residenceOnly,
      internationalCount,
      domesticCount,
      directionEstablishedCount
    },
    events
  };
}

// --------------------------------------------------------------------------
// Master Calculation Entry Point
// --------------------------------------------------------------------------

/**
 * Calculates Natal Relocation Promise, Transits & Direction with rigorous location separation
 * and genuine Level-2 chart-specific astronomical dynamism across selectable horizons.
 */
export function calculateRelocationEventSignature(
  kundali: CompleteKundali,
  lang: Language = 'en',
  options?: RelocationCalculationOptions
): RelocationEventSignature {
  const isHi = lang === 'hi';
  const dasha = getActiveDasha(kundali);
  const focusDt = options?.focusDate ? new Date(options.focusDate) : new Date();
  const focusDateStr = focusDt.toISOString().split('T')[0];
  const selectedHorizon: RelocationTimeHorizon = options?.timeHorizon || '1Y';

  // Strict location profile
  const locationProfile = buildRelocationLocationProfile(kundali, options);
  const { birthPlace, currentResidenceStatus, currentResidence, referenceLocation } = locationProfile;

  const planets = kundali.planets || [];
  const houses = kundali.houses || [];

  // Extract key lords and placements
  const fourthHouse = houses.find(h => h.houseNumber === 4);
  const ninthHouse = houses.find(h => h.houseNumber === 9);
  const tenthHouse = houses.find(h => h.houseNumber === 10);
  const twelfthHouse = houses.find(h => h.houseNumber === 12);
  const thirdHouse = houses.find(h => h.houseNumber === 3);

  const rahuPl = planets.find(p => p.name === 'Rahu');
  const moonPl = planets.find(p => p.name === 'Moon');
  const saturnPl = planets.find(p => p.name === 'Saturn');
  const jupiterPl = planets.find(p => p.name === 'Jupiter');

  const fourthLord = (fourthHouse?.signLord as PlanetName) || 'Moon';
  const ninthLord = (ninthHouse?.signLord as PlanetName) || 'Jupiter';
  const tenthLord = (tenthHouse?.signLord as PlanetName) || 'Mars';
  const twelfthLord = (twelfthHouse?.signLord as PlanetName) || 'Venus';
  const thirdLord = (thirdHouse?.signLord as PlanetName) || 'Mercury';

  const fourthLordPl = planets.find(p => p.name === fourthLord);
  const tenthLordPl = planets.find(p => p.name === tenthLord);
  const twelfthLordPl = planets.find(p => p.name === twelfthLord);

  // Real Ashtakavarga Bindus
  const b4 = getHouseBindus(kundali, 4);
  const b12 = getHouseBindus(kundali, 12);
  const b10 = getHouseBindus(kundali, 10);
  const b9 = getHouseBindus(kundali, 9);
  const b3 = getHouseBindus(kundali, 3);

  // --------------------------------------------------------------------------
  // 1. Dual Gochar & Multi-Axis Activation Calculation
  // --------------------------------------------------------------------------
  const activeAxes: string[] = [];
  let isFourthActive = false;
  let isTenthActive = false;
  let isTwelfthActive = false;
  let isNinthActive = false;
  let isThirdActive = false;

  const relevantLords = [fourthLord, tenthLord, twelfthLord, ninthLord, thirdLord, 'Rahu', 'Moon', 'Jupiter', 'Saturn'] as PlanetName[];

  const dashaPlanets = [dasha.maha, dasha.antar, dasha.pratyantar];
  if (dashaPlanets.includes(fourthLord) || dashaPlanets.includes('Moon') || fourthLordPl?.house === 12) {
    isFourthActive = true;
    activeAxes.push('4th House (Residence/Home)');
  }
  if (dashaPlanets.includes(tenthLord) || dashaPlanets.includes('Sun') || tenthLordPl?.house === 10) {
    isTenthActive = true;
    activeAxes.push('10th House (Career/Enterprise)');
  }
  if (dashaPlanets.includes(twelfthLord) || dashaPlanets.includes('Rahu') || (rahuPl && [4, 9, 12].includes(rahuPl.house))) {
    isTwelfthActive = true;
    activeAxes.push('12th House (Distance/Away-from-Home)');
  }
  if (dashaPlanets.includes(ninthLord) || dashaPlanets.includes('Jupiter') || (jupiterPl && [9, 12].includes(jupiterPl.house))) {
    isNinthActive = true;
    activeAxes.push('9th House (Long-Distance Travel/Dharma)');
  }
  if (dashaPlanets.includes('Mercury') || (moonPl && moonPl.house === 3)) {
    isThirdActive = true;
    activeAxes.push('3rd House (Short-Distance Movement)');
  }

  // --------------------------------------------------------------------------
  // 2. Parashari D-4 (Chaturthamsha) Calculation
  // --------------------------------------------------------------------------
  const d4Analysis = calculateParashariD4(kundali, fourthLord, twelfthLord, lang);

  // --------------------------------------------------------------------------
  // 3. Dynamic Timing Window & Peak Date Calculation for Primary Focus Window
  // --------------------------------------------------------------------------
  const dynamicTiming = calculateDynamicRelocationWindow(kundali, focusDt, relevantLords, lang);
  const { startDate, endDate, peakDate, startDateObj, endDateObj, currentStatus, activePratyantardasha, dashaResonanceScore, peakDetails } = dynamicTiming;

  // --------------------------------------------------------------------------
  // 4. Foreign / Overseas Quad-Classification & Settlement Check
  // --------------------------------------------------------------------------
  const foreignTravelScore = isTwelfthActive || isNinthActive ? 85 : 40;
  const foreignStayScore = isTwelfthActive && (isTenthActive || isFourthActive) ? 78 : 35;
  const foreignRelocationScore = isTwelfthActive && isFourthActive && isTenthActive ? 72 : 28;
  const foreignSettlementScore = isTwelfthActive && fourthLordPl?.house === 12 && saturnPl?.house === 12 ? 68 : (isTwelfthActive && d4Analysis.mobilityNature === 'HIGH_MOBILITY' ? 58 : 22);

  const foreignAssessment: ForeignQuadAssessment = {
    foreignTravel: foreignTravelScore >= 50,
    foreignTravelConfidence: foreignTravelScore,
    foreignStay: foreignStayScore >= 50,
    foreignStayConfidence: foreignStayScore,
    foreignRelocation: foreignRelocationScore >= 50,
    foreignRelocationConfidence: foreignRelocationScore,
    foreignSettlement: foreignSettlementScore >= 50,
    foreignSettlementConfidence: foreignSettlementScore,
    twelfthHouseContextEn: '12th House activation indicates expenditure of domestic energy, distance, or remote environment. It corroborates separation from familiar surroundings with supportive D-4 Chaturthamsha signatures.',
    twelfthHouseContextHi: 'द्वादश भाव का सक्रियण घरेलू ऊर्जा के व्यय, दूरी अथवा दूरस्थ वातावरण को दर्शाता है। यह चतुर्थांश (D-4) वर्ग चक्र के आधार पर जन्मस्थान से पृथकता का पुष्ट संकेत देता है।'
  };

  // --------------------------------------------------------------------------
  // 5. Calendar Duration Arithmetic & Open-Ended Transitions
  // --------------------------------------------------------------------------
  const isSettlement = foreignSettlementScore >= 60 || (isFourthActive && isTwelfthActive && d4Analysis.d4MobilityScore >= 65);
  const durationDetails = calculateRelocationDuration(startDateObj, endDateObj, isSettlement, d4Analysis.d4MobilityScore, lang);
  const { durationDays, durationClass, isOpenEnded } = durationDetails;

  // --------------------------------------------------------------------------
  // 6. Movement Type, Residence Change & Driver Classification
  // --------------------------------------------------------------------------
  let movementType: MovementType = 'TRAVEL';
  let residenceChange: ResidenceChangeLikelihood = 'MODERATE';
  let primaryDriver: RelocationDriver = 'CAREER_PROFESSION';
  let sequence: RelocationSequence = 'CAREER_THEN_RELOCATION';
  let geographicScale: GeographicScale = 'ANOTHER_REGION_STATE';
  let birthplaceRelation: BirthplaceRelationship = 'AWAY_FROM_BIRTHPLACE';

  if (isFourthActive && isTenthActive && isTwelfthActive) {
    movementType = 'RELOCATION';
    residenceChange = 'HIGH_LIKELIHOOD';
    primaryDriver = 'CAREER_PROFESSION';
    geographicScale = 'LONG_DISTANCE';
    sequence = 'SIMULTANEOUS_DUAL';
  } else if (isFourthActive && isTwelfthActive) {
    movementType = 'RELOCATION';
    residenceChange = 'HIGH_LIKELIHOOD';
    primaryDriver = 'FAMILY_HOME';
    geographicScale = 'LONG_DISTANCE';
    sequence = 'RESIDENCE_THEN_LATER_CAREER';
  } else if (isTenthActive && isTwelfthActive) {
    movementType = 'EXTENDED_STAY';
    residenceChange = 'MODERATE';
    primaryDriver = 'CAREER_PROFESSION';
    geographicScale = 'LONG_DISTANCE';
    sequence = 'CAREER_THEN_RELOCATION';
  } else if (isNinthActive && !isFourthActive) {
    movementType = 'TRAVEL';
    residenceChange = 'UNLIKELY';
    primaryDriver = 'EDUCATION';
    geographicScale = 'LONG_DISTANCE';
    sequence = 'UNSPECIFIED';
  } else if (isThirdActive && !isTwelfthActive) {
    movementType = 'TRAVEL';
    residenceChange = 'MODERATE';
    primaryDriver = 'BUSINESS_VENTURE';
    geographicScale = 'NEARBY_CITY';
    birthplaceRelation = 'NEAR_BIRTHPLACE';
  } else {
    movementType = 'TRAVEL';
    residenceChange = 'MODERATE';
    primaryDriver = 'CAREER_PROFESSION';
    geographicScale = 'SAME_REGION_STATE';
  }

  // --------------------------------------------------------------------------
  // 7. Direction Reference & Guardrail Validation
  // --------------------------------------------------------------------------
  const requestedOriginRef: DirectionReferencePoint = options?.originReference || 'CURRENT_RESIDENCE';

  let directionAvailable = false;
  let direction: CardinalDirection = 'NOT_ESTABLISHED';
  let effectiveDirectionRef: DirectionReferencePoint = 'NONE';
  let directionReferenceLabelEn = 'Geographic Reference: Not available (Current residence required)';
  let directionReferenceLabelHi = 'भौगोलिक संदर्भ: उपलब्ध नहीं (वर्तमान निवास आवश्यक)';
  let generalizedInterpretationOnly = false;
  let promptUserForLocation = false;

  const locationPrompt = {
    required: false,
    messageEn: 'To assess the direction of a possible relocation, I need your current residence. Your birthplace cannot be used as your current location. Please provide your current city/country.',
    messageHi: 'संभावित स्थानांतरण की दिशा जानने हेतु आपके वर्तमान निवास का विवरण आवश्यक है। आपके जन्मस्थान को वर्तमान स्थान के रूप में प्रयुक्त नहीं किया जा सकता। कृपया अपना वर्तमान नगर/देश प्रदान करें।'
  };

  const twelfthSign = twelfthHouse?.sign || 'Pisces';

  if (referenceLocation) {
    directionAvailable = true;
    effectiveDirectionRef = requestedOriginRef;

    if (SIGN_DIRECTIONS[twelfthSign]) {
      direction = SIGN_DIRECTIONS[twelfthSign];
    } else if (GRAHA_DIRECTIONS[ninthLord]) {
      direction = GRAHA_DIRECTIONS[ninthLord];
    } else {
      direction = 'NORTH';
    }

    if (effectiveDirectionRef === 'BIRTHPLACE') {
      directionReferenceLabelEn = `Direction from Birthplace (${referenceLocation.locationValue})`;
      directionReferenceLabelHi = `जन्मस्थान से दिशा (${referenceLocation.locationValue})`;
    } else if (effectiveDirectionRef === 'CURRENT_RESIDENCE') {
      directionReferenceLabelEn = `Direction from Current Residence (${referenceLocation.locationValue})`;
      directionReferenceLabelHi = `वर्तमान निवास से दिशा (${referenceLocation.locationValue})`;
    } else if (effectiveDirectionRef === 'PREVIOUS_RESIDENCE') {
      directionReferenceLabelEn = `Direction from Previous Residence (${referenceLocation.locationValue})`;
      directionReferenceLabelHi = `पूर्व निवास से दिशा (${referenceLocation.locationValue})`;
    } else {
      directionReferenceLabelEn = `Direction from Selected Origin (${referenceLocation.locationValue})`;
      directionReferenceLabelHi = `चयनित मूल स्थान से दिशा (${referenceLocation.locationValue})`;
    }
  } else {
    directionAvailable = false;
    direction = 'NOT_ESTABLISHED';
    effectiveDirectionRef = 'NONE';
    generalizedInterpretationOnly = true;
    promptUserForLocation = true;
    locationPrompt.required = true;
    directionReferenceLabelEn = 'Geographic Reference: Not available (Current residence required)';
    directionReferenceLabelHi = 'भौगोलिक संदर्भ: उपलब्ध नहीं (वर्तमान निवास आवश्यक)';
  }

  // --------------------------------------------------------------------------
  // 8. India vs International Jurisdiction Classification
  // --------------------------------------------------------------------------
  const geographicContext = classifyGeographicContext(
    referenceLocation ? referenceLocation.locationValue : null,
    locationProfile.selectedDestination ? locationProfile.selectedDestination.locationValue : null,
    foreignRelocationScore,
    foreignTravelScore
  );

  if (foreignRelocationScore >= 70) {
    if (directionAvailable) {
      geographicScale = 'FOREIGN_OVERSEAS';
      birthplaceRelation = 'AWAY_FROM_BIRTHPLACE';
    } else {
      geographicScale = 'LONG_DISTANCE';
    }
  }

  // --------------------------------------------------------------------------
  // 9. Directional Provenance & Sanskrit Attribution
  // --------------------------------------------------------------------------
  const directionProvenance = deriveDirectionProvenance(
    direction,
    referenceLocation ? referenceLocation.locationValue : null,
    twelfthSign,
    ninthLord,
    lang
  );

  // --------------------------------------------------------------------------
  // 10. Transparent 5-Part Weighted Evidence Matrix
  // --------------------------------------------------------------------------
  let gocharScore = 55;
  if (saturnPl && [4, 10, 12].includes(saturnPl.house)) gocharScore += 15;
  if (jupiterPl && [4, 9, 12].includes(jupiterPl.house)) gocharScore += 15;
  if (rahuPl && [4, 9, 12].includes(rahuPl.house)) gocharScore += 10;

  const { items: evidenceBreakdown, confidenceScore, evidenceStrength } = calculateEvidenceBreakdown(
    isFourthActive,
    isTenthActive,
    isTwelfthActive,
    isNinthActive,
    dashaResonanceScore,
    b4,
    b12,
    b9,
    d4Analysis.d4MobilityScore,
    gocharScore
  );

  // --------------------------------------------------------------------------
  // 11. Career + Residence Temporal Overlap Modeling
  // --------------------------------------------------------------------------
  const careerResidenceOverlap = calculateCareerResidenceOverlap(
    isFourthActive,
    isTenthActive,
    dasha,
    tenthLord,
    fourthLord,
    startDate,
    endDate,
    lang
  );

  // --------------------------------------------------------------------------
  // 12. Human Comprehension 9-Point Breakdown
  // --------------------------------------------------------------------------
  const whereTextEn = directionAvailable
    ? `${direction} (${directionReferenceLabelEn})`
    : 'Generalized relocation potential active. Explicit direction requires your current residence or selected reference point.';

  const whereTextHi = directionAvailable
    ? `${direction} (${directionReferenceLabelHi})`
    : 'सामान्य स्थानांतरण प्रभाव सक्रिय है। विशिष्ट दिशा जानने हेतु वर्तमान निवास अथवा संदर्भ बिंदु आवश्यक है।';

  const fromWhatTextEn = directionAvailable
    ? effectiveDirectionRef === 'BIRTHPLACE'
      ? `Moving away from birthplace (${referenceLocation?.locationValue}).`
      : `Transitioning from confirmed reference origin (${referenceLocation?.locationValue}).`
    : 'Transitioning from current living environment (Birthplace is strictly birth data and is never assumed to be your current home).';

  const fromWhatHi = directionAvailable
    ? effectiveDirectionRef === 'BIRTHPLACE'
      ? `जन्मस्थान (${referenceLocation?.locationValue}) से दूरी का योग।`
      : `पुष्ट संदर्भ मूल स्थान (${referenceLocation?.locationValue}) से स्थानांतरण।`
    : 'वर्तमान रहन-सहन के परिवेश से परिवर्तन (जन्मस्थान को स्वतः वर्तमान निवास नहीं माना जाता)।';

  const howFarTextEn = directionAvailable
    ? geographicScale === 'FOREIGN_OVERSEAS'
      ? 'Cross-border / Overseas environment'
      : geographicScale === 'LONG_DISTANCE'
      ? 'Long-distance domestic or inter-state relocation'
      : 'Regional / Nearby metropolitan area'
    : 'Distance and scale depend on your current residence relative to potential destinations.';

  const howFarTextHi = directionAvailable
    ? geographicScale === 'FOREIGN_OVERSEAS'
      ? 'विदेश / समुद्र-पार प्रवास का योग'
      : geographicScale === 'LONG_DISTANCE'
      ? 'दूरस्थ अंतर-राज्यीय अथवा दीर्घ दूरी प्रवास'
      : 'निकटवर्ती क्षेत्रीय अथवा महानगरीय स्थानांतरण'
    : 'दूरी एवं विस्तार का निर्धारण वर्तमान निवास के सापेक्ष किया जाता है।';

  const whatIsUncertainEn = directionAvailable
    ? 'Specific city or neighborhood names cannot be deterministically inferred from natal chart symbolism alone; timing represents prime energetic windows.'
    : 'Current residence was not provided. Direction and relative distance cannot be calculated without an explicit reference origin; birthplace is never used as a proxy for current location.';

  const whatIsUncertainHi = directionAvailable
    ? 'विशिष्ट देश अथवा नगर के नाम की ज्योतिषीय बाध्यता नहीं होती; यह गणना अनुकूल काल-खंड एवं संभावना को दर्शाती है।'
    : 'वर्तमान निवास उपलब्ध नहीं कराया गया है। संदर्भ बिंदु के अभाव में दिशा व दूरी की गणना नहीं की जा सकती; जन्मस्थान को वर्तमान स्थान नहीं माना जाता।';

  const humanComprehension = {
    whatEn: movementType === 'RELOCATION'
      ? 'Sustained Residence Change & Relocation'
      : movementType === 'EXTENDED_STAY'
      ? 'Extended Professional Assignment'
      : 'Temporary Travel & Exploration',
    whatHi: movementType === 'RELOCATION'
      ? 'दीर्घकालिक निवास परिवर्तन एवं पुनर्वास'
      : movementType === 'EXTENDED_STAY'
      ? 'विस्तारित कार्य प्रवास'
      : 'अल्पकालिक यात्रा एवं भ्रमण',
    whyEn: primaryDriver === 'CAREER_PROFESSION'
      ? 'Career advancement, enterprise expansion, and professional duties.'
      : 'Family circumstances, home establishment, or personal development.',
    whyHi: primaryDriver === 'CAREER_PROFESSION'
      ? 'आजीविका में प्रगति, कार्य विस्तार एवं व्यावसायिक दायित्व।'
      : 'पारिवारिक परिस्थितियां अथवा व्यक्तिगत विकास।',
    whenEn: `${startDate} – ${endDate} (Peak resonance: ${peakDate})`,
    whenHi: `${startDate} से ${endDate} के मध्य (शीर्ष प्रभाव: ${peakDate})`,
    whereEn: whereTextEn,
    whereHi: whereTextHi,
    fromWhatEn: fromWhatTextEn,
    fromWhatHi,
    howFarEn: howFarTextEn,
    howFarHi: howFarTextHi,
    howLongEn: durationDetails.durationDisplayEn,
    howLongHi: durationDetails.durationDisplayHi,
    howStrongEn: `${evidenceStrength} evidence (${confidenceScore}% convergence score, SAV 4H: ${b4}, 12H: ${b12})`,
    howStrongHi: `${evidenceStrength === 'STRONG' ? 'प्रबल' : 'मध्यम'} साक्ष्य (${confidenceScore}% सहमति स्कोर, अष्टकवर्ग ४था भाव: ${b4}, १२वां: ${b12})`,
    whatIsUncertainEn,
    whatIsUncertainHi
  };

  // --------------------------------------------------------------------------
  // 13. RG-HORIZON-EXT-V6.0 Extended Horizon Discovery
  // --------------------------------------------------------------------------
  const horizonDiscovery = discoverHorizonRelocationWindows(
    kundali,
    focusDt,
    selectedHorizon,
    dynamicTiming,
    {
      eventType: careerResidenceOverlap.careerResidenceCoupling ? 'Career + Residence Transition' : 'Geographic Movement Signature',
      primaryDriver,
      direction,
      directionReference: effectiveDirectionRef,
      directionReferenceLabelEn,
      directionReferenceLabelHi,
      geographicScale,
      referenceLocationValue: referenceLocation ? referenceLocation.locationValue : null,
      directionAvailable,
      confidenceScore,
      evidenceStrength,
      b4,
      b12,
      d4Analysis,
      isIndianOrigin: geographicContext.originJurisdiction === 'INDIA_DOMESTIC'
    },
    lang
  );

  // --------------------------------------------------------------------------
  // 14. AI Gateway Payload Enforcement
  // --------------------------------------------------------------------------
  const prohibitedClaims: string[] = [];
  if (!directionAvailable) {
    prohibitedClaims.push(
      'Specific directional movements (North, South, East, West)',
      'Movement away from current residence',
      'Movement toward birthplace',
      'Specific country, state, or city destination names',
      'Specific distance or proximity to current home'
    );
  }

  const aiGatewayPayload: RelocationAIGatewayPayload = {
    birthPlace: birthPlace.locationValue,
    currentResidence: currentResidence ? currentResidence.locationValue : null,
    currentResidenceStatus,
    referenceLocation: referenceLocation ? referenceLocation.locationValue : null,
    selectedOrigin: locationProfile.selectedOrigin ? locationProfile.selectedOrigin.locationValue : null,
    selectedDestination: locationProfile.selectedDestination ? locationProfile.selectedDestination.locationValue : null,
    locationStatus: directionAvailable ? 'READY_WITH_REFERENCE' : promptUserForLocation ? 'LOCATION_REQUIRED' : 'GENERALIZED_NO_REFERENCE',
    natalEventSignatureOnly: !directionAvailable,
    prohibitedClaims
  };

  // --------------------------------------------------------------------------
  // 15. Deterministic Cache Key (Includes Horizon)
  // --------------------------------------------------------------------------
  const cacheKey = generateRelocationCacheKey(
    kundali.id || kundali.birthDetails?.name || 'native',
    focusDateStr,
    selectedHorizon,
    referenceLocation ? referenceLocation.locationValue : null,
    locationProfile.selectedOrigin ? locationProfile.selectedOrigin.locationValue : null,
    locationProfile.selectedDestination ? locationProfile.selectedDestination.locationValue : null
  );

  return {
    eventType: careerResidenceOverlap.careerResidenceCoupling ? 'Career + Residence Transition' : 'Geographic Movement Signature',
    currentStatus,
    startDate,
    endDate,
    peakDate,
    focusDate: focusDateStr,
    confidenceScore,
    evidenceStrength,

    // Horizon Discovery
    selectedHorizon,
    horizonDiscovery,

    // Strict Location Profile
    locationProfile,

    // Dimensions
    movementType,
    residenceChange,
    geographicScale,
    directionAvailable,
    direction,
    directionReference: effectiveDirectionRef,
    directionReferenceLabelEn,
    directionReferenceLabelHi,
    birthplaceRelation,
    primaryDriver,
    sequence,
    durationClass,
    isOpenEnded,

    // Level-2 Deep Forensic Objects
    durationDetails,
    peakDetails,
    careerResidenceOverlap,
    evidenceBreakdown,
    d4Analysis,
    geographicContext,
    directionProvenance,

    // Generalization & User Prompting
    generalizedInterpretationOnly,
    promptUserForLocation,
    locationPrompt,

    // Foreign Quad Assessment
    foreignAssessment,

    // Human Comprehension Breakdown
    humanComprehension,

    // Dual Gochar
    dualGocharSynthesis: {
      careerResidenceCoupling: careerResidenceOverlap.careerResidenceCoupling,
      activeAxes,
      descriptionEn: careerResidenceOverlap.careerResidenceCoupling
        ? 'Simultaneous activation across 10th (Career) and 4th (Residence) creates a coupled Career-Led Residence Transition event signature.'
        : 'Active transits stimulate geographic movement with focus on professional and personal realignment.',
      descriptionHi: careerResidenceOverlap.careerResidenceCoupling
        ? 'दशम (आजीविका) एवं चतुर्थ (निवास) भावों का एक साथ जाग्रत होना कार्य-प्रेरित निवास परिवर्तन योग का निर्माण करता है।'
        : 'गोचर एवं दशा का प्रभाव व्यावसायिक व व्यक्तिगत संरेखण हेतु स्थान-परिवर्तन का संकेत देता है।'
    },

    // Acharya Forensics
    acharyaForensics: {
      relevantBhavas: [4, 9, 10, 12, 3],
      bhaveshMatrix: [
        { house: 4, lord: fourthLord, placementHouse: fourthLordPl?.house || 4, strength: `SAV ${b4} Bindus` },
        { house: 10, lord: tenthLord, placementHouse: tenthLordPl?.house || 10, strength: `SAV ${b10} Bindus` },
        { house: 12, lord: twelfthLord, placementHouse: twelfthLordPl?.house || 12, strength: `SAV ${b12} Bindus` },
        { house: 9, lord: ninthLord, placementHouse: 9, strength: `SAV ${b9} Bindus` }
      ],
      operatingDasha: `${dasha.maha} MD / ${dasha.antar} AD / ${activePratyantardasha} PD`,
      vargaCorroboration: [
        {
          varga: 'D-4 Chaturthamsha',
          significance: 'Fixed Assets, Land & Domestic Base',
          supportive: d4Analysis.supportive
        },
        { varga: 'D-9 Navamsha', significance: 'General Fruit of Karma & Luck', supportive: true },
        { varga: 'D-12 Dvadasamsha', significance: 'Ancestral Roots & Remote Lineage', supportive: false }
      ],
      btrSensitivity: 'STABLE',
      contradictions: [
        `4th house exhibits ${b4} Ashtakavarga bindus (standard threshold 28), establishing ${b4 >= 28 ? 'grounding stability' : 'lower residential inertia'}. 12th house has ${b12} bindus.`
      ],
      classicalCitationEn: 'BPHS: "When the 4th lord and 12th lord are conjunct or aspecting each other under Rahu influence, the native establishes dwelling in distant lands or across water."',
      classicalCitationHi: 'बृहत्पाराशर होराशास्त्र: यदा चतुर्थेश-द्वादशेशौ राहु-संबन्धेन युतौ भवतः, तदा जातकस्य दूरदेशे सागर-तट-वासे वा वास-योगो भवति।',
      shlokaSanskrit: 'चतुर्थे व्ययनाथे वा व्यये चतुर्थनायके। विदेशवासं कुरुते स्वदेशाद् भ्रंशमेव च॥ (बृहत्पाराशर होराशास्त्र)'
    },

    cacheKey,
    aiGatewayPayload
  };
}
